import styled from '@emotion/styled';

import { Input, Label, Select, Typography } from '@/design-system';
import { theme } from '@/design-system/theme';
import { CryptoAsset } from '@/shared/modules/wallet/use-crypto-assets';

import { CoinIcon } from '../../crypto-assets/coin-icon';
import { fmtAmount, fmtUsdShort } from '../../crypto-assets/formatters';

const Root = styled('div')(({ theme: t }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: t.spacing[4],
}));

const Row = styled('div')(({ theme: t }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: t.spacing[8],
}));

const AmountBox = styled('div')(({ theme: t }) => ({
  flex: 1,
  minWidth: 0,

  '& input': {
    fontSize: 18,
    fontWeight: 500,
    lineHeight: '28px',
  },

  '& > .send-amount-input > div:focus-within': {
    boxShadow: `inset 0 0 0 2px ${t.colors.white[20]}`,
  },
}));

const TokenBox = styled('div')({
  flexShrink: 0,
  width: 144,
});

const MetaRow = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 8,
});

interface SendAmountFieldProps {
  assets: CryptoAsset[];
  coin: CryptoAsset;
  amount: string;
  amountUsd: number | null;
  error?: string;
  onAmountChange: (_value: string) => void;
  onCoinChange: (_coinType: string) => void;
  amountDisabled?: boolean;
}

export const SendAmountField = ({
  assets,
  coin,
  amount,
  amountUsd,
  error,
  onAmountChange,
  onCoinChange,
  amountDisabled,
}: SendAmountFieldProps) => (
  <Root>
    <Label>Amount</Label>
    <Row>
      <AmountBox>
        <Input
          name="amount"
          size="xl"
          containerClassName="send-amount-input"
          type="text"
          inputMode="decimal"
          autoComplete="off"
          value={amount}
          placeholder="0.00"
          disabled={amountDisabled}
          state={error ? 'destructive' : 'default'}
          helperText={error}
          aria-label="Amount"
          onChange={(event) => {
            if (/^\d*(?:\.\d*)?$/.test(event.target.value)) {
              onAmountChange(event.target.value);
            }
          }}
        />
      </AmountBox>
      <TokenBox>
        <Select
          size="xl"
          ariaLabel="Asset"
          value={coin.coinType}
          onChange={onCoinChange}
          startIcon={<CoinIcon symbol={coin.symbol} iconUrl={coin.iconUrl} size={20} />}
          options={assets.map((asset) => ({
            value: asset.coinType,
            label: asset.symbol,
            icon: <CoinIcon symbol={asset.symbol} iconUrl={asset.iconUrl} size={20} />,
          }))}
        />
      </TokenBox>
    </Row>
    <MetaRow>
      <Typography variant="body3" color={theme.colors.text.primary}>
        {amountUsd == null ? '' : fmtUsdShort(amountUsd)}
      </Typography>
      <Typography variant="body3" color={theme.colors.text.secondary}>
        Balance:{' '}
        <Typography as="span" variant="body3" color={theme.colors.text.primary}>
          {coin.usdValue == null
            ? `${fmtAmount(coin.balance)} ${coin.symbol}`
            : fmtUsdShort(coin.usdValue)}
        </Typography>
      </Typography>
    </MetaRow>
  </Root>
);
